import React, { ComponentProps } from 'react'
import {
  Typography,
  List,
  Divider,
  ListItem,
  ListItemSecondaryAction,
  IconButton,
  Box,
} from '@material-ui/core'
import produce from 'immer'

import AddIcon from '@material-ui/icons/Add'
import DeleteIcon from '@material-ui/icons/Delete'

import GenericInput from '../GenericInput'
import { IInputProps, TEntityOverrideProp } from 'src/types'

type TProps<ItemType extends NonNullable<unknown> = Record<string, any>> =
  IInputProps<ItemType[]> & {
    itemProps?: Partial<ComponentProps<typeof GenericInput>>
    itemInitialValue?: ItemType
  }

const ObjectArrayInput = <
  ItemType extends NonNullable<unknown> = Record<string, any>,
>({
  property,
  onChange,
  value,
  name,
  itemProps = {},
  itemInitialValue,
}: TProps<ItemType>): JSX.Element => {
  const listItems = value ?? []

  return (
    <Box width='100%'>
      <Typography variant='body1'>{property.displayedName ?? name}</Typography>
      <List>
        {listItems.map((item, i) => [
          <ListItem key={i}>
            <GenericInput
              // items is always set on array properties reaching this input
              property={property.items as TEntityOverrideProp}
              onChange={e => {
                onChange(
                  produce(listItems, it => {
                    it[i] = e
                  }),
                )
              }}
              value={item}
              // item inputs legitimately carry no own form name
              {...(itemProps as Omit<
                ComponentProps<typeof GenericInput>,
                'property' | 'onChange' | 'value'
              >)}
            />
            {property.readOnly !== true ? (
              <ListItemSecondaryAction>
                <IconButton
                  edge='end'
                  aria-label='delete'
                  onClick={() => {
                    onChange(
                      produce(listItems, it => {
                        it.splice(i, 1)
                      }),
                    )
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            ) : null}
          </ListItem>,
          <Divider key={`${i}-divider`} />,
        ])}
        <ListItem>
          <IconButton
            edge='end'
            aria-label='add'
            onClick={() =>
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
              onChange([...listItems, itemInitialValue ?? ({} as ItemType)])
            }
          >
            <AddIcon />
          </IconButton>
        </ListItem>
      </List>
    </Box>
  )
}

export default ObjectArrayInput
